import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Grid
} from '@mui/material';
import { nonConformanceService } from '../services/nonConformanceService';
import { eledgerService } from '../services/eledgerService';

const NC_TYPES = ['incomplete_allocation', 'missing_supplier_data', 'late_transfer', 'verification_dispute', 'calculation_error', 'principle_violation', 'other'];
const SEVERITIES = ['critical', 'high', 'medium', 'low', 'informational'];

function NonConformances() {
  const [ledgers, setLedgers] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState('');
  const [ncs, setNcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ nc_type: 'other', severity: 'medium', title: '', description: '' });

  useEffect(() => {
    eledgerService.getAll().then(data => setLedgers(data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedLedger) loadNCs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLedger]);

  const loadNCs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nonConformanceService.getByLedger(selectedLedger);
      setNcs(data ?? []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await nonConformanceService.create({ ...form, e_ledger_id: selectedLedger });
      setDialogOpen(false);
      setForm({ nc_type: 'other', severity: 'medium', title: '', description: '' });
      loadNCs();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    }
  };

  const getSeverityColor = (sev) => {
    const map = { critical: 'error', high: 'error', medium: 'warning', low: 'info', informational: 'default' };
    return map[sev] || 'default';
  };

  const getStatusColor = (status) => {
    if (status === 'resolved' || status === 'closed') return 'success';
    if (status === 'open') return 'error';
    return 'warning';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Non-Conformances</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track deviations from E-ledger principles and corrective actions
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Select E-Ledger</InputLabel>
              <Select value={selectedLedger} label="Select E-Ledger" onChange={(e) => setSelectedLedger(e.target.value)}>
                {ledgers.map(l => <MenuItem key={l.e_ledger_id} value={l.e_ledger_id}>{l.ledger_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button variant="contained" disabled={!selectedLedger} onClick={() => setDialogOpen(true)}>
              Report Non-Conformance
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : selectedLedger && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Non-Conformances ({ncs.length})</Typography>
          {ncs.length === 0 ? (
            <Alert severity="success">No non-conformances recorded for this ledger</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ncs.map((nc) => (
                  <TableRow key={nc.non_conformance_id}>
                    <TableCell>{nc.title}</TableCell>
                    <TableCell><Chip label={(nc.nc_type || 'unknown').replace(/_/g, ' ')} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={nc.severity} size="small" color={getSeverityColor(nc.severity)} /></TableCell>
                    <TableCell><Chip label={nc.status} size="small" color={getStatusColor(nc.status)} /></TableCell>
                    <TableCell>{new Date(nc.detected_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Non-Conformance</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={form.nc_type} label="Type" onChange={(e) => setForm({ ...form, nc_type: e.target.value })}>
                  {NC_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select value={form.severity} label="Severity" onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.title || !form.description}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NonConformances;
